import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, defer, EMPTY, Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, exhaustMap, filter, finalize, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { MatChip, MatChipInputEvent } from '@angular/material/chips';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FilterByPipe } from '../../../pipes';
import { SelectAllConfigInterface } from './models';
import { SelectAllDefaultsConfig } from './configs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { MatOptionSelectionChange } from '@angular/material/core';
import { ResponseDto } from '../../../dto';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { MATERIAL_ICON_TO_ICONPROP } from '../../../icons/material-icon-to-fontawesome-iconprop-map';
import { SI_VIRTUAL_OPTION_ITEM_SIZE_PX } from '../../../constants';

interface ChipsSelectorAngularIconConfig {
    values: unknown[];
    class: string;
    icon: string;
}

export interface ChipsSelectorStoreParams {
    page: number;
    pageSize: number;
    name?: string;
}

export interface ChipsSelectorStore<T> {
    getData: (params: ChipsSelectorStoreParams) => Observable<ResponseDto<T>>;
    /**
     * Optional hook to resolve preselected values into full option objects (so chips can render labels).
     * Should return all items found for the given ids.
     */
    getByIds?: (ids: Array<string | number>) => Observable<T[]>;
}

@UntilDestroy()
@Component({
    selector: 'app-chips-selector-form-field-ui',
    templateUrl: './chips-selector-form-field.component.html',
    styleUrls: ['./chips-selector-from-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipsSelectorFormFieldComponent),
            multi: true,
        },
        FilterByPipe,
    ],
    standalone: false
})
export class ChipsSelectorFormFieldComponent<T> implements ControlValueAccessor, OnInit, AfterViewInit {
    @Input() set items(value: T[]) {
        if (this.store) {
            // In store mode the component owns the options list.
            return;
        }
        this.innerItemsSubject.next(value);
    }
    get items() {
        return this.innerItemsSubject.getValue();
    }
    @Input() set maxHeightPx(value: number) {
        if (value === undefined || value === 0) {
          this.maximumHeightPx = this.defaultMaxHeight;
        } else {
          this.maximumHeightPx = value > 64 ? value : 64;
        }
    }
    get maxHeightPx() {
        return this.maximumHeightPx;
    }

    @Input() filterKeyName: string;
    @Input() bindLabel: string;
    @Input() bindValue: string;
    @Input() label: string;
    @Input() placeholder: string;
    /**
     * When set, the dropdown will not show real items for short search strings.
     * Example: minSearchLength=3 means 1-2 characters shows a single informational message option.
     */
    @Input() minSearchLength: number = 0;
    /**
     * Controls whether the component filters `items` locally using `filterKeyName`.
     * Set to false for server-side search where the parent provides already-filtered items.
     */
    @Input() useClientSideFilter: boolean = true;
    /**
     * By default the component clears the search when overlay closes.
     * Set to false to preserve search string and dropdown contents between focus changes.
     */
    @Input() resetSearchOnClose: boolean = true;
    /**
     * When provided, the component switches to "store mode" where it internally loads/searches/paginates options.
     * This is additive and does not affect existing usages that pass `[items]`.
     */
    @Input() set store(value: ChipsSelectorStore<T> | null) {
        this.innerStoreSubject.next(value);
        this.resetStoreState();
        // keep selected items visible as chips even before overlay opens
        this.hydrateSelectedItemsFromStore(this.currentValueKeys);
    }
    get store(): ChipsSelectorStore<T> | null {
        return this.innerStoreSubject.getValue();
    }
    /** Page size passed to store.getData (store may ignore it). */
    @Input() storePageSize: number = 250;
    /**
     * If true, once the unfiltered (empty-term) list is fully loaded, the component will not call the store for
     * searches anymore and will perform search filtering locally on the full unfiltered dataset.
     */
    @Input() enableLocalFilterAfterUnfilteredFullyLoaded: boolean = false;
    @Input() set disabled(value: boolean) {
        this._disabled = value;
        if (value) {
            this.filterControl.disable();
            return;
        }
        this.filterControl.enable();
    }
    get disabledDropdown(): boolean {
        return this._disabled;
    }
    @Input() disabledChips: boolean;
    @Input() maxSelectedItems: number = 100;
    @Input() set isLoading(value: boolean) {
        this._externalIsLoading = !!value;
        this.syncIsLoading();
    }
    get isLoading(): boolean {
        return this._externalIsLoading || this._internalLoadingCount > 0;
    }
    @Input() showClear: boolean = true;
    /**
     * When true, `writeValue()` treats incoming form values as the chip values (no lookup in `items`).
     * Intended for string-only cases (e.g. free-text keywords). In this mode `selectionChanged` emits raw strings.
     */
    @Input() skipItemsCheck: boolean = false;
    @Input('class') classList: string;
    @Input() set selectAllConfig(config: SelectAllConfigInterface) {
        if (config) {
            this.innerSelectAllConfig = {
                ...SelectAllDefaultsConfig,
                ...config,
            }
        }
    }
    get selectAllConfig() {
        return this.innerSelectAllConfig;
    }
    @Input() set angularIcon(value: Partial<ChipsSelectorAngularIconConfig> | null | undefined) {
        const normalized: ChipsSelectorAngularIconConfig = {
            values: value?.values ?? [],
            class: value?.class ?? '',
            icon: value?.icon ?? '',
        };

        this.innerAngularIcon = normalized;
        this.angularIconIconProp = normalized.icon ? (MATERIAL_ICON_TO_ICONPROP[normalized.icon] ?? null) : null;
    }
    get angularIcon(): ChipsSelectorAngularIconConfig {
        return this.innerAngularIcon;
    }
    @Input() listElementTooltipIfTruncated: boolean = true;

    @Output() selectionChanged: EventEmitter<T[]> = new EventEmitter<T[]>();
    @Output() limitExceeded: EventEmitter<void> = new EventEmitter<void>();
    @Output() searchChanged: EventEmitter<string> = new EventEmitter<string>();
    @Output() triggerSearch: EventEmitter<void> = new EventEmitter<void>();
    @Output() panelOpened: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() panelClosed: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() virtualScrollEnd: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ContentChild('preOption') preOptionTemplate: TemplateRef<any>;
    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
    @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;
    @ViewChild(CdkConnectedOverlay) overlay!: CdkConnectedOverlay;
    @ViewChild('origin', { read: ElementRef }) originRef!: ElementRef;
    @ViewChild('chipGrid', { read: ElementRef }) chipGridRef!: ElementRef<HTMLDivElement>;
    @ViewChildren(MatChip) chipList!: QueryList<MatChip>;

    chips: { key: string | number, value: T }[] = [];
    angularIconIconProp: IconProp | null = null;
    filterControl: FormControl = new FormControl();
    filteredItems$: Observable<T[]>;
    defaultMaxHeight = 250;

    readonly valueSubject: BehaviorSubject<Array<string | number>> = new BehaviorSubject<Array<string | number>>([]);
    readonly itemsSize: number = SI_VIRTUAL_OPTION_ITEM_SIZE_PX;
    private _disabled: boolean = false;
    private _externalIsLoading: boolean = false;
    private _internalLoadingCount: number = 0;
    private isOptionJustAdded: boolean = false;
    private isSelectionChanged: boolean = false;
    private innerItemsSubject: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
    private onChanged: (value: any) => void;
    private innerAngularIcon: ChipsSelectorAngularIconConfig = { values: [], class: '', icon: '' };
    private onTouched: () => void;
    private innerSelectAllConfig: SelectAllConfigInterface = SelectAllDefaultsConfig;
    private scrollEvent$ = new Subject<void>();
    private readonly isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private maximumHeightPx:number = this.defaultMaxHeight;
    private readonly noResultFoundOption = "No results found";
    private readonly innerStoreSubject: BehaviorSubject<ChipsSelectorStore<T> | null> = new BehaviorSubject<ChipsSelectorStore<T> | null>(null);
    private isPanelOpen: boolean = false;

    // Store-mode state
    private storeSearchTerm: string = '';
    private storePage: number = 0;
    private storeTotalCount: number | null = null;
    private unfilteredPage: number = 0;
    private unfilteredTotalCount: number | null = null;
    private unfilteredFullyLoaded: boolean = false;
    private readonly unfilteredOptionsByKey: Map<string | number, T> = new Map<string | number, T>();
    private readonly storeSearchTermSubject: Subject<string> = new Subject<string>();
    private readonly storeLoadMoreSubject: Subject<void> = new Subject<void>();
    private readonly storeCancelSubject: Subject<void> = new Subject<void>();
    private readonly selectedItemsByKey: Map<string | number, T> = new Map<string | number, T>();
    private readonly currentOptionsByKey: Map<string | number, T> = new Map<string | number, T>();
    private currentValueKeys: Array<string | number> = [];

    constructor (
        private readonly filterByPipe: FilterByPipe,
        private readonly cdr: ChangeDetectorRef,
        private readonly zone: NgZone,
    ) {}

    ngOnInit(): void {
        this.setFilteredItems();
        this.scrollEvent$.pipe(debounceTime(200), untilDestroyed(this)).subscribe(() => {
            this.checkScrollPosition();
        });

        // Store-mode search (page 1)
        this.storeSearchTermSubject.pipe(
            map((raw: string) => (raw ?? '').toString().trim()),
            distinctUntilChanged(),
            filter(() => !!this.store),
            // only perform requests for valid term lengths
            switchMap((term: string) => {
                // do nothing if panel is not open (but still allow preselected hydration)
                if (!this.isPanelOpen) {
                    this.storeSearchTerm = term;
                    return EMPTY;
                }

                this.storeSearchTerm = term;

                // 1) Empty term: show the already loaded unfiltered list (if any) without requesting again.
                if (!term) {
                    if (this.unfilteredOptionsByKey.size > 0) {
                        this.applyUnfilteredCacheAsActive();
                        return EMPTY;
                    }

                    // First open: request the unfiltered first page.
                    this.resetStorePaging();
                    this.currentOptionsByKey.clear();
                    this.syncStoreItemsToInnerItems();

                    this.setInternalLoading(true);
                    const termSnapshot = '';
                    return this.fetchStorePage(1, termSnapshot).pipe(
                        takeUntil(this.storeCancelSubject),
                        map((res: ResponseDto<T>) => ({ term: termSnapshot, res })),
                        finalize(() => this.setInternalLoading(false)),
                        catchError(() => EMPTY),
                    );
                }

                // 2) Short search: don't request (UI shows informational message option)
                if (this.minSearchLength > 0 && term.length > 0 && term.length < this.minSearchLength) {
                    return EMPTY;
                }

                // 3) Local filtering (only after full unfiltered load)
                if (this.enableLocalFilterAfterUnfilteredFullyLoaded && this.unfilteredFullyLoaded) {
                    this.applyUnfilteredCacheAsActive();
                    return EMPTY;
                }

                // 4) Server-side search: reset + request page 1 for the new term
                this.resetStorePaging();
                this.currentOptionsByKey.clear();
                this.syncStoreItemsToInnerItems();

                this.setInternalLoading(true);
                const termSnapshot = term;
                return this.fetchStorePage(1, termSnapshot).pipe(
                    takeUntil(this.storeCancelSubject),
                    map((res: ResponseDto<T>) => ({ term: termSnapshot, res })),
                    finalize(() => this.setInternalLoading(false)),
                    catchError(() => EMPTY),
                );
            }),
            untilDestroyed(this),
        ).subscribe(({ term, res }: { term: string; res: ResponseDto<T> }) => {
            if (!res) return;
            if ((term ?? '').toString().trim() !== (this.storeSearchTerm ?? '').toString().trim()) return;

            // page 1 results
            const termSnapshot = (term ?? '').toString().trim();

            // Unfiltered (empty term) – fill / extend the persistent unfiltered cache
            if (!termSnapshot) {
                const page = res?.page || 1;
                const pageSize = res?.pageSize || this.storePageSize;

                if (page === 1) {
                    this.unfilteredOptionsByKey.clear();
                    this.unfilteredTotalCount = null;
                    this.unfilteredFullyLoaded = false;
                }

                this.unfilteredPage = page;
                this.updateUnfilteredTotalCount(res?.totalCount);

                (res?.content || []).forEach((item: T) => {
                    const key = this.getItemByBindValue(item) as any;
                    if (key !== null && key !== undefined) {
                        this.unfilteredOptionsByKey.set(key, item);
                    }
                });

                // Determine completeness: prefer totalCount, fallback to partial page
                if (this.unfilteredTotalCount !== null) {
                    this.unfilteredFullyLoaded = this.unfilteredOptionsByKey.size >= this.unfilteredTotalCount;
                } else if ((res?.content || []).length < pageSize) {
                    this.unfilteredFullyLoaded = true;
                }

                this.applyUnfilteredCacheAsActive();
                return;
            }

            // Server-search mode (non-empty term)
            this.storePage = res?.page || 1;
            // Some backends may not return totalCount consistently for subsequent pages.
            // Keep totalCount monotonic (never decrease) to avoid prematurely stopping paging.
            this.storeTotalCount = null;
            this.updateStoreTotalCount(res?.totalCount);

            this.currentOptionsByKey.clear();
            (res?.content || []).forEach((item: T) => {
                const key = this.getItemByBindValue(item) as any;
                if (key !== null && key !== undefined) {
                    this.currentOptionsByKey.set(key, item);
                }
            });
            this.syncStoreItemsToInnerItems();
            this.hydrateSelectedItemsFromStore(this.currentValueKeys);
            this.rebuildChipsFromValue(this.currentValueKeys);
            this.cdr.detectChanges();
        });

        // Store-mode load more (page 2+)
        this.storeLoadMoreSubject.pipe(
            filter(() => !!this.store && this.isPanelOpen),
            exhaustMap(() => {
                if (this.isLoading) return EMPTY;

                const term = (this.storeSearchTerm ?? '').toString().trim();
                if (this.minSearchLength > 0 && term.length > 0 && term.length < this.minSearchLength) return EMPTY;

                // Once the full unfiltered list is loaded, searches are handled locally (no paging requests).
                if (this.enableLocalFilterAfterUnfilteredFullyLoaded && this.unfilteredFullyLoaded && term.length > 0) {
                    return EMPTY;
                }

                // Unfiltered mode: stop requesting once fully loaded (even if totalCount is missing).
                if (!term && this.unfilteredFullyLoaded) {
                    return EMPTY;
                }

                if (this.storeTotalCount !== null && this.currentOptionsByKey.size >= this.storeTotalCount) {
                    return EMPTY;
                }

                const nextPage = (this.storePage || 0) + 1;
                if (nextPage <= 1) return EMPTY;

                this.setInternalLoading(true);
                const termSnapshot = term;
                return this.fetchStorePage(nextPage, termSnapshot).pipe(
                    takeUntil(this.storeCancelSubject),
                    map((res: ResponseDto<T>) => ({ term: termSnapshot, res })),
                    finalize(() => this.setInternalLoading(false)),
                    catchError(() => EMPTY),
                );
            }),
            untilDestroyed(this),
        ).subscribe(({ term, res }: { term: string; res: ResponseDto<T> }) => {
            if (!res) return;
            if ((term ?? '').toString().trim() !== (this.storeSearchTerm ?? '').toString().trim()) return;

            const termSnapshot = (term ?? '').toString().trim();

            // Unfiltered paging
            if (!termSnapshot) {
                const pageSize = res?.pageSize || this.storePageSize;

                this.unfilteredPage = res?.page || this.unfilteredPage;
                this.updateUnfilteredTotalCount(res?.totalCount);

                (res?.content || []).forEach((item: T) => {
                    const key = this.getItemByBindValue(item) as any;
                    if (key !== null && key !== undefined) {
                        this.unfilteredOptionsByKey.set(key, item);
                    }
                });

                if (this.unfilteredTotalCount !== null) {
                    this.unfilteredFullyLoaded = this.unfilteredOptionsByKey.size >= this.unfilteredTotalCount;
                } else if ((res?.content || []).length < pageSize) {
                    this.unfilteredFullyLoaded = true;
                }

                this.applyUnfilteredCacheAsActive();
                return;
            }

            // Server-search paging
            this.storePage = res?.page || this.storePage;
            this.updateStoreTotalCount(res?.totalCount);

            (res?.content || []).forEach((item: T) => {
                const key = this.getItemByBindValue(item) as any;
                if (key !== null && key !== undefined) {
                    this.currentOptionsByKey.set(key, item);
                }
            });
            this.syncStoreItemsToInnerItems();
            this.cdr.detectChanges();
        });
    }

    ngAfterViewInit() {
        const resizeObserver = new ResizeObserver(() => {
            this.overlay.overlayRef?.updatePosition();
        });
        resizeObserver.observe(this.originRef.nativeElement);
    }

    openOverlay() {
        this.isPanelOpen = true;
        this.panelOpened.emit(true);
        this.searchInput.nativeElement.focus();
        this.viewPort?.checkViewportSize?.();

        if (this.store) {
            // initial load for current search term (usually empty on first open)
            this.storeSearchTermSubject.next(this.filterControl?.value || '');
        }
    }

    closeOverlay() {
        this.isPanelOpen = false;
        this.panelOpened.emit(false);
        this.storeCancelSubject.next();
        this.searchInput.nativeElement.blur();
        this.onTouched?.();
        this.onOverlayClosed();
    }

    onChevronClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.disabledDropdown) {
            return;
        }

        if (this.isPanelOpen) {
            this.closeOverlay();
            return;
        }

        this.openOverlay();
    }

    onOverlayOutsideClick(event: MouseEvent): void {
        const target = event?.target as HTMLElement | null;
        if (target && this.originRef?.nativeElement?.contains?.(target)) {
            // Clicking within the origin (input/chips) should not close the overlay.
            // This also enables single-click switching between multiple dropdown inputs.
            return;
        }
        this.closeOverlay();
    }

    onMatChipInputTokenEnd(value: MatChipInputEvent): void {
        if (!this.isOptionJustAdded && !value?.value?.length && (this.chips.length || this.isSelectionChanged)) {
            this.triggerSearch.emit();
            this.isSelectionChanged = false;
        }
        this.isOptionJustAdded = false;
    }

    isChipsContain(option: T): boolean {
        return !!this.chips.find((chip) => chip.key == this.getItemByBindValue(option));
    }

    sortChips() {
        this.chips.sort((a, b) => {
            const aValue = (a.value?.[this.bindLabel] || a.value).toUpperCase();
            const bValue = (b.value?.[this.bindLabel] || b.value).toUpperCase();
            if (aValue < bValue) return -1;
            if (aValue > bValue) return 1;
            return 0;
        });
    }

    onSelectOption(event: MatOptionSelectionChange, value: T): void {
        if (!event.isUserInput) return;

        const option: T = value;

        if (this.isMessageOption(option)) {
            event.source.deselect();
            return;
        }

        if (typeof option !== 'undefined' && !this.isChipsContain(option)) {
            if (this.chips.length < this.maxSelectedItems) {
                const found: T = this.items?.find((item: T) => this.getItemByBindValue(item) === option);
                const chipData = found || option;
                this.chips.push({ key: this.getItemByBindValue(chipData), value: chipData });
                this.setValue();
                this.isSelectionChanged = true;
                this.isOptionJustAdded = true;
                this.searchInput.nativeElement.focus();

                queueMicrotask(() => {
                    const cont = this.chipGridRef.nativeElement;
                    cont.scrollTop = cont.scrollHeight;
                    this.chipList.last?._elementRef.nativeElement
                        .scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    this.overlay?.overlayRef?.updatePosition?.();
                });
            } else {
                this.limitExceeded.emit();
            }
        }

        event.source.deselect();

        this.filteredItems$.pipe(take(1)).subscribe(() => {
            if (this.isNoItemsInList()) {
                this.closeOverlay();
            }
        })
    }

    onSelectAll(selectItems: T[]): void {
        selectItems.forEach((option: T) => {
            if (!this.isChipsContain(option)) {
                if (this.chips.length < this.maxSelectedItems) {
                    const found: T = this.items?.find((item: T) => this.getItemByBindValue(item) === option);
                    const chipData = found || option;
                    this.chips.push({ key: this.getItemByBindValue(chipData), value: chipData })
                } else {
                    this.limitExceeded.emit();
                }
            }
        })

        this.setValue();
        this.isSelectionChanged = true;
        this.isOptionJustAdded = true;
        this.searchInput.nativeElement.focus();

        this.filteredItems$.pipe(take(1)).subscribe(() => {
            if (this.isNoItemsInList()) {
                this.closeOverlay();
            }
        });
    }

    removeItem(chipId: string | number): void {
        this.chips = this.chips.filter((chip) => chip.key !== chipId);
        this.setValue();
    }

    clearAll(): void {
        this.chips = [];
        this.setValue();
        // Clearing selected chips is a full reset action; clear the search term as well to keep UI consistent.
        this.clearSearch();
        this.closeOverlay();
    }

    writeValue(value: any): void {
        const normalizedKeys = (Array.isArray(value) ? value : []) as Array<string | number>;
        this.currentValueKeys = normalizedKeys as Array<string | number>;

        // Keep the valueSubject in sync immediately (needed for filtering selected items out of the dropdown)
        this.valueSubject.next(this.currentValueKeys);

        // Store mode: we can hydrate chips without waiting for `[items]` to be populated externally.
        if (this.store) {
            this.rebuildChipsFromValue(this.currentValueKeys);
            this.hydrateSelectedItemsFromStore(this.currentValueKeys);
            this.cdr.detectChanges();
            return;
        }

        this.innerItemsSubject.pipe(
          map((items: T[]) => this.skipItemsCheck ? this.skipItemsCheck : !!items?.length),
          filter(Boolean),
          take(1),
          untilDestroyed(this),
        ).subscribe(() => {
            this.chips = [];
            if (value?.length) {
                value.forEach((key: string | number) => {
                    if (this.skipItemsCheck) {
                        this.chips.push({key, value: key as unknown as T})
                        return;
                    }
                    const found: T = this.items?.find((item: T) => this.getItemByBindValue(item) == key);
                    if (found) {
                        this.chips.push({key:this.getItemByBindValue(found), value:found})
                    }
                });
            }
            this.cdr.detectChanges();
        })
    }

    registerOnChange(fn: any): void {
        this.onChanged = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    private isNoItemsInList(): boolean {
        return !this.viewPort?.getDataLength();
    }

    private setValue(): void {
        const value = this.chips.map((chip) => chip.key);
        const fullValue = this.chips.map((chip) => chip.value);
        // Keep internal value cache in sync with user-driven changes.
        // In template-driven forms, Angular does not always call writeValue() back on the same control
        // after onChanged() is invoked, so store-mode refreshes must rely on this cache.
        this.currentValueKeys = value as Array<string | number>;
        this.valueSubject.next(value);
        this.onChanged(value);
        this.selectionChanged.emit(fullValue);

        if (this.store) {
            this.selectedItemsByKey.clear();
            this.chips.forEach((chip) => {
                const key = chip.key;
                if (key !== null && key !== undefined) {
                    this.selectedItemsByKey.set(key, chip.value as T);
                }
            });
            this.syncStoreItemsToInnerItems();
        }
    }

    private setFilteredItems(): void {
        // UI filter stream: must start with current control value (overlay can be destroyed/recreated).
        const filterControlValueForUi$ = defer(() => {
            const currentValue = typeof this.filterControl?.value === 'string' ? this.filterControl.value : '';
            return this.filterControl.valueChanges.pipe(
                debounceTime(500),
                startWith(currentValue),
            );
        });

        // Requests/output stream: keep existing semantics (debounced).
        const filterControlValueForRequests$ = this.filterControl.valueChanges.pipe(
            startWith(''),
            debounceTime(500),
        );

        this.filteredItems$ = combineLatest([
            this.innerItemsSubject.asObservable(),
            filterControlValueForUi$,
            this.valueSubject.asObservable(),
            this.isLoadingSubject.asObservable(),
        ]).pipe(
            map(([items, search, value, isLoading]: [T[], string, Array<string | number>, boolean]) => {
                const searchValue = typeof search === 'string' ? search : '';
                const trimmedSearchValue = searchValue?.trim?.() ?? '';

                if (this.minSearchLength > 0 && trimmedSearchValue.length > 0 && trimmedSearchValue.length < this.minSearchLength) {
                    return this.buildMessageOptionList(this.getMinSearchLengthMessage());
                }

                const isStoreLocalFilteringActive =
                    !!this.store
                    && this.enableLocalFilterAfterUnfilteredFullyLoaded
                    && this.unfilteredFullyLoaded;

                const filteredSourceList = (this.useClientSideFilter && (!this.store || isStoreLocalFilteringActive))
                    ? this.filterByPipe.transform(items || [], searchValue, this.filterKeyName)
                    : (items || []);

                const filteredList = filteredSourceList
                    .filter((item: T) => !value?.includes(typeof item === 'object' && this.bindValue ? item[this.bindValue] : item))

                    if (filteredList.length) {
                        return filteredList;
                    }

                    if (trimmedSearchValue.length > 0) {
                        // When a server-side request is in progress, don't show "No results found" flicker.
                        // Keep existing items (if any) or hide the dropdown until results arrive.
                        if (isLoading) {
                            return [];
                        }
                        return this.buildMessageOptionList(this.noResultFoundOption);
                    }

                    return [];
                }
            ),
        );

        filterControlValueForRequests$.pipe(untilDestroyed(this)).subscribe((value: string) => {
            this.searchChanged.emit(value);
            if (this.store && this.isPanelOpen) {
                this.storeSearchTermSubject.next(value);
            }
        });
    }

    private getItemByBindValue(item: T) {
        return typeof item === 'object' && this.bindValue ? item[this.bindValue] : item;
    }

    isMessageOption(option: T): boolean {
        const label = this.getOptionLabel(option);
        if (!label) return false;
        if (label === this.noResultFoundOption) return true;
        if (this.minSearchLength > 0 && label === this.getMinSearchLengthMessage()) return true;
        return false;
    }

    private getOptionLabel(option: any): string {
        if (option === null || option === undefined) return '';
        if (!this.bindLabel) return option?.toString?.() ?? '';
        return option?.[this.bindLabel]?.toString?.() ?? '';
    }

    private getMinSearchLengthMessage(): string {
        return `At least ${this.minSearchLength} characters required`;
    }

    private buildMessageOptionList(message: string): any[] {
        if (!message) return [];
        if (!this.bindLabel) {
            return [message];
        }
        return [{ [this.bindValue || 'id']: 0, [this.bindLabel]: message }];
    }

    onClose(): void {
        this.clearSearch();
        this.sortChips();
    }

    private onOverlayClosed() {
        if (this.resetSearchOnClose) {
            this.clearSearch();
        }
        this.sortChips();
    }

    private clearSearch() {
        this.searchInput.nativeElement.value = '';
        this.filterControl.setValue('');
    }

    onVirtualScroll() {
        this.scrollEvent$.next();
    }

    private checkScrollPosition() {
        const offsetBottom = this.viewPort?.measureScrollOffset('bottom');
        const offsetTop = this.viewPort?.measureScrollOffset('top');
        if (offsetTop !== 0 && offsetBottom < 200 && !this.isLoading) {
            this.virtualScrollEnd.emit();
            if (this.store) {
                this.storeLoadMoreSubject.next();
            }
        }
    }

    private fetchStorePage(page: number, term: string): Observable<ResponseDto<T>> {
        const store = this.store;
        if (!store) return EMPTY;
        const params: ChipsSelectorStoreParams = {
            page,
            pageSize: this.storePageSize,
            ...(term?.length ? { name: term } : {}),
        };
        return store.getData(params);
    }

    private resetStorePaging(): void {
        this.storePage = 0;
        this.storeTotalCount = null;
    }

    private updateStoreTotalCount(candidate: any): void {
        if (typeof candidate !== 'number') return;
        if (!Number.isFinite(candidate) || candidate < 0) return;

        if (this.storeTotalCount === null) {
            this.storeTotalCount = candidate;
            return;
        }

        // Never decrease totalCount across pages for the same query.
        if (candidate >= this.storeTotalCount) {
            this.storeTotalCount = candidate;
        }
    }

    private resetStoreState(): void {
        if (!this.store) return;
        this.resetStorePaging();
        this.storeSearchTerm = '';
        this.unfilteredPage = 0;
        this.unfilteredTotalCount = null;
        this.unfilteredFullyLoaded = false;
        this.unfilteredOptionsByKey.clear();
        this.selectedItemsByKey.clear();
        this.currentOptionsByKey.clear();
        this.syncStoreItemsToInnerItems();
    }

    private updateUnfilteredTotalCount(candidate: any): void {
        if (typeof candidate !== 'number') return;
        if (!Number.isFinite(candidate) || candidate < 0) return;

        if (this.unfilteredTotalCount === null) {
            this.unfilteredTotalCount = candidate;
            return;
        }

        // Never decrease totalCount across pages for the same dataset.
        if (candidate >= this.unfilteredTotalCount) {
            this.unfilteredTotalCount = candidate;
        }
    }

    private applyUnfilteredCacheAsActive(): void {
        // Active paging counters (used by loadMore) should reflect the unfiltered dataset.
        this.storePage = this.unfilteredPage;
        this.storeTotalCount = this.unfilteredTotalCount;

        this.currentOptionsByKey.clear();
        this.unfilteredOptionsByKey.forEach((item: T, key: string | number) => {
            this.currentOptionsByKey.set(key, item);
        });

        this.syncStoreItemsToInnerItems();
        this.hydrateSelectedItemsFromStore(this.currentValueKeys);
        this.rebuildChipsFromValue(this.currentValueKeys);
        this.cdr.detectChanges();
    }

    private syncStoreItemsToInnerItems(): void {
        if (!this.store) return;
        const merged = new Map<string | number, T>();
        this.selectedItemsByKey.forEach((item, key) => merged.set(key, item));
        this.currentOptionsByKey.forEach((item, key) => {
            if (!merged.has(key)) {
                merged.set(key, item);
            }
        });
        this.innerItemsSubject.next(Array.from(merged.values()));
    }

    private rebuildChipsFromValue(keys: Array<string | number>): void {
        this.chips = [];
        if (!keys?.length) {
            this.cdr.detectChanges();
            return;
        }

        keys.forEach((key) => {
            if (this.skipItemsCheck) {
                this.chips.push({ key, value: key as unknown as T });
                return;
            }

            const resolved =
                this.selectedItemsByKey.get(key) ||
                this.currentOptionsByKey.get(key) ||
                this.items?.find((item: T) => this.getItemByBindValue(item) == key);

            if (resolved) {
                this.chips.push({ key, value: resolved });
            }
        });
    }

    private hydrateSelectedItemsFromStore(keys: Array<string | number>): void {
        const store = this.store;
        if (!store?.getByIds) return;
        if (!keys?.length) return;

        const missing = keys.filter((k) => !this.selectedItemsByKey.has(k) && !this.currentOptionsByKey.has(k));
        if (!missing.length) return;

        this.setInternalLoading(true);
        store.getByIds(missing).pipe(
            take(1),
            takeUntil(this.storeCancelSubject),
            catchError(() => of([] as T[])),
            finalize(() => {
                this.setInternalLoading(false);
            }),
            untilDestroyed(this),
        ).subscribe((items: T[]) => {
            if (!items?.length) return;

            items.forEach((item: T) => {
                const key = this.getItemByBindValue(item) as any;
                if (key !== null && key !== undefined) {
                    this.selectedItemsByKey.set(key, item);
                }
            });

            this.syncStoreItemsToInnerItems();
            this.rebuildChipsFromValue(this.currentValueKeys);
            this.cdr.detectChanges();
        });
    }

    private setInternalLoading(value: boolean): void {
        if (value) {
            this._internalLoadingCount++;
        } else {
            this._internalLoadingCount = Math.max(0, this._internalLoadingCount - 1);
        }
        this.syncIsLoading();
    }

    private syncIsLoading(): void {
        this.isLoadingSubject.next(this.isLoading);
    }
}
